import { AlertTriangle } from 'lucide-react';
import { useTickets } from '../hooks/useTickets';
import type { Ticket } from '../types';

// ── Helpers ────────────────────────────────────────────────────────────────

function resolvedDate(t: Ticket): Date {
  return new Date(t.sourceUpdatedAt ?? t.updatedAt);
}

function getWeekBuckets(tickets: Ticket[]): number[] {
  // Returns [week-4-count, week-3-count, week-2-count, this-week-count]
  const now = Date.now();
  const buckets = [0, 0, 0, 0];
  for (const t of tickets) {
    if (t.status !== 'complete') continue;
    const daysAgo = (now - resolvedDate(t).getTime()) / 86400000;
    if (daysAgo <= 7) buckets[3]++;
    else if (daysAgo <= 14) buckets[2]++;
    else if (daysAgo <= 21) buckets[1]++;
    else if (daysAgo <= 28) buckets[0]++;
  }
  return buckets;
}

function getMedianCycleTime(tickets: Ticket[]): number | null {
  const times = tickets
    .filter((t) => t.status === 'complete' && t.createdAt && (t.sourceUpdatedAt || t.updatedAt))
    .map((t) => (resolvedDate(t).getTime() - new Date(t.sourceCreatedAt ?? t.createdAt).getTime()) / 86400000)
    .filter((d) => d > 0)
    .sort((a, b) => a - b);
  if (!times.length) return null;
  const mid = Math.floor(times.length / 2);
  return times.length % 2 === 0 ? (times[mid - 1] + times[mid]) / 2 : times[mid];
}

// ── Sub-components ─────────────────────────────────────────────────────────

function ThroughputChart({ buckets }: { buckets: number[] }) {
  const max = Math.max(...buckets, 1);
  const labels = ['Wk -4', 'Wk -3', 'Wk -2', 'This wk'];

  return (
    <div className="flex items-end gap-2">
      {buckets.map((count, i) => (
        <div key={i} className="flex flex-col items-center gap-1">
          <div
            className="w-9 rounded-t bg-primary/60 dark:bg-primary/50 transition-all"
            style={{ height: `${Math.max((count / max) * 48, count > 0 ? 4 : 2)}px` }}
          />
          <span className="text-meta font-medium text-gray-600 dark:text-gray-400 tabular-nums">{count}</span>
          <span className="text-meta text-gray-400 dark:text-gray-500 leading-tight text-center whitespace-nowrap">{labels[i]}</span>
        </div>
      ))}
    </div>
  );
}

function PanelSkeleton() {
  return (
    <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5">
      <div className="skeleton h-3 w-24 rounded mb-4" />
      <div className="flex gap-8">
        <div className="space-y-4">
          <div className="skeleton h-12 w-32 rounded" />
          <div className="skeleton h-3 w-20 rounded" />
        </div>
        <div className="space-y-4">
          <div className="skeleton h-6 w-20 rounded" />
          <div className="skeleton h-3 w-16 rounded" />
        </div>
        <div className="space-y-4">
          <div className="skeleton h-6 w-16 rounded" />
          <div className="skeleton h-3 w-14 rounded" />
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

interface VelocityPanelProps {
  projectId: string;
  staleAfterDays?: number;
}

export function VelocityPanel({ projectId, staleAfterDays = 5 }: VelocityPanelProps) {
  const { data: tickets, isLoading } = useTickets(projectId);

  if (isLoading) return <PanelSkeleton />;

  const all = tickets ?? [];

  if (all.length === 0) {
    return (
      <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5">
        <p className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
          Delivery Pulse
        </p>
        <p className="text-meta text-gray-400 dark:text-gray-500">No ticket data yet</p>
      </div>
    );
  }

  const buckets = getWeekBuckets(all);
  const medianCycleTime = getMedianCycleTime(all);
  const now = Date.now();
  const staleThresholdMs = staleAfterDays * 86400000;
  const DONE = new Set(['complete', 'done', 'closed', 'cancelled', 'wont_fix', 'invalid', 'idea']);
  const staleCount = all.filter((t) => {
    if (DONE.has(t.status)) return false;
    const sourceTs = t.sourceUpdatedAt ? new Date(t.sourceUpdatedAt).getTime() : 0;
    const localTs  = t.updatedAt        ? new Date(t.updatedAt).getTime()        : 0;
    return Math.max(sourceTs, localTs) > 0 && now - Math.max(sourceTs, localTs) > staleThresholdMs;
  }).length;

  return (
    <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5">
      <p className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-6">
        Delivery Pulse
      </p>
      <div className="flex flex-wrap gap-8 items-end">
        {/* Throughput */}
        <div>
          <p className="text-meta text-gray-500 dark:text-gray-400 mb-2">Throughput (4 wk)</p>
          <ThroughputChart buckets={buckets} />
        </div>

        {/* Cycle time */}
        <div className="pb-6">
          <p className="text-meta text-gray-500 dark:text-gray-400 mb-1">Cycle Time</p>
          <p className="text-title font-semibold text-gray-900 dark:text-white tabular-nums">
            {medianCycleTime !== null ? `${medianCycleTime.toFixed(1)}d` : '—'}
          </p>
          <p className="text-meta text-gray-400 dark:text-gray-500 mt-0.5">
            {medianCycleTime !== null ? 'median per ticket' : 'no complete tickets'}
          </p>
        </div>

        {/* Stale tickets */}
        <div className="pb-6">
          <p className="text-meta text-gray-500 dark:text-gray-400 mb-1">Stale Tickets</p>
          <div className="flex items-center gap-1.5">
            {staleCount > 0 && (
              <AlertTriangle className="h-4 w-4 text-amber-500 dark:text-amber-400 flex-shrink-0" />
            )}
            <p
              className={`text-title font-semibold tabular-nums ${
                staleCount > 0
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-gray-900 dark:text-white'
              }`}
            >
              {staleCount}
            </p>
          </div>
          <p className="text-meta text-gray-400 dark:text-gray-500 mt-0.5">
            &gt;{staleAfterDays} days no update
          </p>
        </div>
      </div>
    </div>
  );
}
